/*
 * Copyright (C) 2020 Reciprocity, Inc - All Rights Reserved
 * Unauthorized use, copying, distribution, displaying, or public performance
 * of this file, via any medium, is strictly prohibited. All information
 * contained herein is proprietary and confidential and may not be shared
 * with any third party without the express written consent of Reciprocity, Inc.
 */

import { join, map } from 'lodash';

export interface AttributeQueryConfig {
  fragment: string; // GraphQL fragment definition
  // TODO: add generics?
  normalize?: (node: any) => any;
}

const defaultAttributeFragment: string = `{
  value
}`;

// TODO: rename to getRegularAttributeFragment
export function getAttributeFragment(objectType: string, attribute: string): string {
  if (!attributesQueryConfig[attribute]) {
    return `${attribute} ${defaultAttributeFragment}`;
  }
  if (attributesQueryConfig[attribute]['fragment'] == undefined) {
    // TODO: TEMP until we move attr type info to backend and use a gql field to determine it on frontend
    // eslint-disable-next-line no-console
    console.error(`Unable to find GraphQL fragment definition for attribute: ${attribute}`);
  }

  // Handles our only case where attribute on different object types requires a
  // different fragment and presenter, on Assessment and Request object.
  const todoListObjectTypes = ['Assessment', 'assessmentsToVerify', 'assessmentsToComplete'];
  const isExceptionObjectType = ['Assessments'].concat(todoListObjectTypes).includes(objectType);
  if (isExceptionObjectType && attribute == 'audit') {
    attribute = 'audits';
  }

  // TODO: change gql schema to return isOverdue in row metadata instead
  // TODO: refactor this!
  const allTodoListContexts = [
    'tasks',
    'tasksToSubmit',
    'tasksToReviewOrVerify',
    'requests',
    'requestsToSubmit',
    'requestsToReviewOrVerify',
    'Assessment',
    'assessmentsToVerify',
    'assessmentsToComplete',
  ];
  if (allTodoListContexts.includes(objectType) && attribute == 'endDate') {
    return `
      endDate {
        value
      }
      isOverdue {
        value
      }
    `;
  }

  return attributesQueryConfig[attribute].fragment;
}

export function getCustomAttributeFragment(customAttributeName: string, customAttributeType: string): string {
  const fragment: string = customAttributesQueryConfig[customAttributeType].fragment;

  return `
    ${customAttributeName}: customAttribute(name: "${customAttributeName}") {
      ... on ${customAttributeType} {
        ${fragment}
      }
    }
  `;
}

export function getMappedAttributeFragment(mappedObjectType: string): string {
  return `
    ${mappedObjectType} {
      edges {
        node {
          id
          sorCode { value }
          sorTitle { value }
        }
      }
    }
  `;
}

const personPresenterAttrs: string[] = ['id', 'name', 'email', 'userRole'];

/**
 * Maps attribute name to attribute name with value field, i.e.:
 * "name" becomes "name { value }"
 */
const valueifyAttrs = (attrs: string[]) => map(attrs, attr => (attr != 'id' ? `${attr} { value }\n` : `${attr}\n`));

const constructFragment = (attrName: string, attrs: string[], connection: boolean = false) => {
  if (connection) {
    return `
      ${attrName} {
        value {
          edges {
            node {
              ${join(valueifyAttrs(attrs), '')}
            }
          }
        }
      }
    `;
  }

  return `
    ${attrName} {
      value {
        ${join(valueifyAttrs(attrs), '')}
      }
    }
  `;
};

const constructCustomAttributeFragment = (attrs: string[]) => {
  if (!attrs.length) {
    return `
      value
    `;
  }

  return `
    value {
      ${join(valueifyAttrs(attrs), '')}
    }
  `;
};

/**
 * Constructs an object that can be passed to a Attribute mutation query as variables.
 *
 * @param attr: Attribute object definition
 * @param val: Value that will be set with the mutation
 */
export const constructAttributeUpdateVariables = (attr: any, val: any) => {
  // Compose variables
  let attrInput = {};
  attrInput[attr.name] = val;
  return {
    attr_input: attrInput,
  };
};

/**
 * Constructs an object that can be passed to a CustomAttribute mutation query
 * as variables.
 *
 * @param attr: CustomAttribute object definition
 * @param fieldType: Field type as a string to use as attribute name for the value
 * @param val: Value that will be set with the mutation
 */
export const constructCustomAttributeUpdateVariables = (attr: any, fieldType: string, val: any) => {
  let caInput = { name: `${attr.name}` };
  caInput[fieldType] = { value: val };
  return {
    ca_input: [caInput],
  };
};

export function constructObjectFragment(modifiedObjectType: string): string {
  return `
    ${modifiedObjectType} {
      value {
        dbId
        objectType
        title
      }
    }
  `;
}

// TODO: consider changing to mapping from presenter type to fragment, to treat all attributes as custom attributes
export const attributesQueryConfig: { [key: string]: AttributeQueryConfig } = {
  id: {
    fragment: `id`,
  },
  contact: {
    fragment: constructFragment('contact', personPresenterAttrs),
  },
  secondaryContact: {
    fragment: constructFragment('secondaryContact', personPresenterAttrs),
  },
  owners: {
    fragment: constructFragment('owners', personPresenterAttrs, true),
  },
  auditManagers: {
    fragment: constructFragment('auditManagers', personPresenterAttrs, true),
  },
  programReaders: {
    fragment: constructFragment('programReaders', personPresenterAttrs, true),
  },
  programEditors: {
    fragment: constructFragment('programEditors', personPresenterAttrs, true),
  },
  programOwners: {
    fragment: constructFragment('programOwners', personPresenterAttrs, true),
  },
  auditors: {
    fragment: constructFragment('auditors', personPresenterAttrs, true),
  },
  modifiedObject: {
    fragment: constructObjectFragment('modifiedObject'),
  },
  modifiedBy: {
    fragment: constructFragment('modifiedBy', personPresenterAttrs, false),
  },
  mappingFrom: {
    fragment: constructObjectFragment('mappingFrom'),
  },
  mappingTo: {
    fragment: constructObjectFragment('mappingTo'),
  },
  people: {
    fragment: constructFragment('people', personPresenterAttrs, true),
  },
  reviewers: {
    fragment: `reviewers {
      value {
        edges {
          node {
            person {
              value {
                id
                name { value }
                email { value }
                userRole { value }
              }
            }
            reviewStatus { value }
          }
        }
      }
    }`,
  },
  principalAssessor: {
    fragment: constructFragment('principalAssessor', personPresenterAttrs),
  },
  secondaryAssessor: {
    fragment: constructFragment('secondaryAssessor', personPresenterAttrs),
  },
  relatedObjects: {
    fragment: `
      relatedObjects {
        value {
          edges {
            node {
              id
              ... on SorNode {
                sorTitle { value }
              }
            }
          }
        }
      }
    `,
  },
  relatedAssessors: {
    fragment: constructFragment('relatedAssessors', personPresenterAttrs, true),
  },
  relatedAssignees: {
    fragment: constructFragment('relatedAssignees', personPresenterAttrs, true),
  },
  relatedCreators: {
    fragment: constructFragment('relatedCreators', personPresenterAttrs, true),
  },
  relatedRequesters: {
    fragment: constructFragment('relatedRequesters', personPresenterAttrs, true),
  },
  relatedVerifiers: {
    fragment: constructFragment('relatedVerifiers', personPresenterAttrs, true),
  },
  ownersGroup: {
    fragment: `
      ownersGroup {
        value {
          edges {
            node {
              ... on Group {
                id
                title { value }
                isPrivate { value }
                people {
                  # We only care about the only person within the private group,
                  # and when it's a non-private group we only care about the
                  # totalCount.
                  value(first: 1) {
                    totalCount
                    edges {
                      node {
                        id
                        name { value }
                        email { value }
                      }
                    }
                  }
                }
              }
            }
          }
        }
      }
    `,
  },
  tags: {
    fragment: `tags {
      value {
        edges {
          node {
            id
            title { value }
            link
          }
        }
      }
    }`,
  },
  documents: {
    fragment: constructFragment('documents', ['id', 'title', 'link'], true),
  },
  fetchers: {
    fragment: constructFragment('fetchers', ['id', 'title'], true),
  },
  recurrenceInfo: {
    // TODO: FIX: gql complains if explicitly requesting the id field ...
    fragment: constructFragment('recurrenceInfo', ['period', 'description']),
  },
  surveyResponses: {
    fragment: `
      surveyResponses {
        value {
          edges {
            node {
              id
              ... on SurveyResponse {
                sorTitle { value }
                publicId { value }
              }
            }
          }
        }
      }
    `,
  },
  assessments: {
    fragment: constructFragment('assessments', ['id', 'title'], true),
  },
  audit: {
    fragment: constructFragment('audit', ['id', 'title']),
  },
  audits: {
    // Exception in attr name for Audits on Assessments
    fragment: constructFragment('audit', ['id', 'title'], true),
  },
  control: {
    fragment: constructFragment('control', ['id', 'title']),
  },
  program: {
    fragment: constructFragment('program', ['id', 'title']),
  },
  relevantAssessments: {
    fragment: constructFragment('relevantAssessments', ['id', 'title'], true),
  },
  relevantAudits: {
    fragment: constructFragment('relevantAudits', ['id', 'title'], true),
  },
  relevantControls: {
    fragment: constructFragment('relevantControls', ['id', 'title'], true),
  },
  relevantPrograms: {
    fragment: constructFragment('relevantPrograms', ['id', 'title'], true),
  },
  endDate: {
    // TODO: change gql schema to return isOverdue in row metadata instead
    fragment: `
      endDate {
        value
      }
    `,
  },
};

export const customAttributesQueryConfig: { [key: string]: AttributeQueryConfig } = {
  CustomAttributeDate: {
    fragment: constructCustomAttributeFragment([]),
  },
  CustomAttributeString: {
    fragment: constructCustomAttributeFragment([]),
  },
  CustomAttributeInt: {
    fragment: constructCustomAttributeFragment([]),
  },
  CustomAttributeFloat: {
    fragment: constructCustomAttributeFragment([]),
  },
  CustomAttributeBoolean: {
    fragment: constructCustomAttributeFragment([]),
  },
  CustomAttributeStringList: {
    fragment: constructCustomAttributeFragment([]),
  },
  // TODO: gql is broken for when there isn't a person mapped
  CustomAttributePerson: {
    fragment: constructCustomAttributeFragment(personPresenterAttrs),
  },
};
